import { useState, useEffect, useCallback } from 'react';

const useSpeechSynthesis = () => {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      setSupported(true);
      
      const loadVoices = () => {
        const availableVoices = window.speechSynthesis.getVoices();
        setVoices(availableVoices);
      };

      loadVoices();
      
      // Les voix peuvent se charger de manière asynchrone
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
    }
  }, []);

  const speak = useCallback((text, options = {}) => {
    if (!supported) {
      console.warn('Speech synthesis not supported');
      return;
    }

    // Arrêter toute lecture en cours
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Configuration par défaut
    utterance.rate = options.rate || 1;
    utterance.pitch = options.pitch || 1;
    utterance.volume = options.volume || 1;
    utterance.lang = options.lang || 'fr-FR';

    // Sélectionner une voix française si disponible
    if (options.voice) {
      utterance.voice = options.voice;
    } else {
      const frenchVoice = voices.find(voice => 
        voice.lang.startsWith('fr') || voice.name.toLowerCase().includes('french')
      );
      if (frenchVoice) {
        utterance.voice = frenchVoice;
      }
    }

    // Événements
    utterance.onstart = () => {
      setSpeaking(true);
      if (options.onStart) options.onStart();
    };

    utterance.onend = () => {
      setSpeaking(false);
      if (options.onEnd) options.onEnd();
    };

    utterance.onerror = (event) => {
      setSpeaking(false);
      console.error('Speech synthesis error:', event);
      if (options.onError) options.onError(event);
    };

    window.speechSynthesis.speak(utterance);
  }, [supported, voices]);

  const stop = useCallback(() => {
    if (supported) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, [supported]);

  const pause = useCallback(() => {
    if (supported) {
      window.speechSynthesis.pause();
    }
  }, [supported]);

  const resume = useCallback(() => {
    if (supported) {
      window.speechSynthesis.resume();
    }
  }, [supported]);

  // Fonction pour lire le contenu d'un élément
  const speakElement = useCallback((elementId, options = {}) => {
    const element = document.getElementById(elementId);
    if (element) {
      const text = element.textContent || element.innerText || '';
      speak(text.trim(), options);
    }
  }, [speak]);

  // Fonction pour lire la page entière
  const speakPage = useCallback((options = {}) => {
    const mainContent = document.getElementById('main') || document.body;
    const text = mainContent.textContent || mainContent.innerText || '';
    // Nettoyer le texte (supprimer les espaces multiples, etc.)
    const cleanText = text.replace(/\s+/g, ' ').trim();
    speak(cleanText, options);
  }, [speak]);

  return {
    speak,
    stop,
    pause,
    resume,
    speakElement,
    speakPage,
    speaking,
    supported,
    voices
  };
};

export default useSpeechSynthesis; 